import { ZERO_TIME } from '@/constants';
import { computeNextBatteryChangeDate } from '@/components/studio/utils/batteryStatusColor';

type SensorBatteryFields = {
  next_battery_change_date?: string | null;
  last_battery_change_date?: string | null;
  estimated_total_runtime_days?: number | null;
};

/**
 * Compute the floor-level "next battery change date" the user should see.
 *
 * Precedence (must match between the read-only display and the editable
 * form — using different orders is what makes a "manual override" silently
 * disappear when sensors gain projections):
 *   1. Floor-level manual override (`savedFloorNext`), if set and not ZERO_TIME.
 *   2. Earliest projection across the floor's sensors. Each sensor projects
 *      either its saved `next_battery_change_date` (if set / non-sentinel) or
 *      `last_battery_change_date + estimated_total_runtime_days` via
 *      `computeNextBatteryChangeDate` — same logic the per-sensor screens use.
 *   3. `undefined` when neither produces a real date.
 *
 * Returns `undefined` (not ZERO_TIME) on no-data so callers don't silently
 * round-trip the sentinel through forms back to the API.
 */
export function computeFloorNextBatteryChangeDate(
  sensors: readonly SensorBatteryFields[],
  savedFloorNext: string | null | undefined,
): string | undefined {
  if (savedFloorNext && savedFloorNext !== ZERO_TIME) return savedFloorNext;

  const projections = sensors
    .map((s) => {
      if (s.next_battery_change_date && s.next_battery_change_date !== ZERO_TIME) {
        return s.next_battery_change_date;
      }
      return computeNextBatteryChangeDate(
        s.last_battery_change_date ?? '',
        s.estimated_total_runtime_days,
      );
    })
    .filter((d): d is string => Boolean(d) && d !== ZERO_TIME);

  if (projections.length === 0) return undefined;
  return projections.toSorted()[0];
}

/**
 * Compute the floor-level "last battery change date" the user should see.
 *
 * Mirror of `computeFloorNextBatteryChangeDate`'s precedence:
 *   1. Floor-level manual override (`savedFloorLast`), if set and not ZERO_TIME.
 *   2. Earliest `last_battery_change_date` across the floor's sensors —
 *      the oldest individual battery service ("the fleet's oldest sensor
 *      hasn't been serviced since this date"). Set on install or each
 *      time the per-sensor "Battery Changed" flow runs.
 *   3. `undefined` when neither produces a real date.
 *
 * Same `undefined` (not ZERO_TIME) return contract as the next-date helper.
 */
export function computeFloorLastBatteryChangeDate(
  sensors: readonly Pick<SensorBatteryFields, 'last_battery_change_date'>[],
  savedFloorLast: string | null | undefined,
): string | undefined {
  if (savedFloorLast && savedFloorLast !== ZERO_TIME) return savedFloorLast;

  const dates = sensors
    .map((s) => s.last_battery_change_date)
    .filter((d): d is string => Boolean(d) && d !== ZERO_TIME);

  if (dates.length === 0) return undefined;
  return dates.toSorted()[0];
}

/**
 * Compute the cascaded Next picker value when the user picks a new Last in
 * the floor edit form — i.e., a "what's the soonest next service if all
 * sensors had this new last_change_date?" preview.
 *
 * Synthesizes a sensor list with each sensor's `last_battery_change_date`
 * overridden to `newLast` and `next_battery_change_date` nulled out (forces
 * the per-sensor projection to recompute from `last + runtime` instead of
 * honoring any saved sensor.next override). Returns the earliest such
 * projection, or ZERO_TIME if no sensor can project (no runtime data,
 * `newLast` is empty/sentinel, etc.).
 *
 * **Design intent (regression-test target):** the cascade is a USER ACTION
 * — picking a new Last is treated as a fleet-service event that
 * invalidates any prior on-disk Next override. Callers using this helper
 * should mark Next as user-picked (via whatever submit-gating mechanism
 * the caller uses) and persist the result on Save. This is intentional
 * per the design's scenario 4 ("user changes Last → Next clears, user
 * would need to re-override if they want"); reviewers occasionally read
 * this as a bug because it diverges from the "auto-filled values stay
 * non-persistent" rule for sensor-derived defaults at form mount, but
 * the cascade is explicitly different — it's a user-driven recompute,
 * not a sensor-derived default.
 *
 * Returns ZERO_TIME (not undefined) so callers can pass the result
 * directly to a form's `setValue` for a `string | undefined`-typed field
 * without needing to coerce — and so the on-the-wire Save explicitly
 * clears any prior override (sending `undefined` would serialize as a
 * field omission and leave the old override on disk).
 */
export function computeCascadedFloorNextFromNewLast(
  sensors: readonly SensorBatteryFields[],
  newLast: string,
): string {
  const synthesized = sensors.map((s) => ({
    ...s,
    last_battery_change_date: newLast,
    next_battery_change_date: null,
  }));
  const projected = computeFloorNextBatteryChangeDate(synthesized, undefined);
  return projected ?? ZERO_TIME;
}
